/**
 * Canonical city list — the single source of truth shared by the Welcome
 * gate, the consumer feed (`/app`), and the operator panel.
 *
 * `id` is a diacritic-free slug stored in `restaurants.city` and passed around
 * in the `?city=` query param. `name` is the display label (with diacritics).
 * Matching is always done on `id` so București/Bucuresti never drift apart.
 *
 * `lat`/`lng` are the city centre (WGS84) — the single source of truth for
 * city geography, used to assign a scraped venue to its nearest city and to
 * sanity-check coordinates. Every county capital of Romania + București is
 * listed, so the scraper can tag venues in any big city; a city only appears
 * (ungreyed) in the picker once it actually has a visible venue.
 *
 * Array order is data only — the Welcome picker lists cities with venues
 * first (A→Z), "soon" cities last (A→Z), and pins the visitor's nearest city
 * on top (location is requested when the picker is first opened); the search
 * box handles the long tail.
 *
 * ⚠ The four original slugs — cluj-napoca, bucuresti, iasi, botosani — are load
 * bearing (already stored on venues); never rename them.
 */

export interface City {
  id: string;
  name: string;
  /** City-centre latitude (WGS84 decimal degrees). */
  lat: number;
  /** City-centre longitude (WGS84 decimal degrees). */
  lng: number;
}

export const CITIES: City[] = [
  { id: "bucuresti", name: "București", lat: 44.4268, lng: 26.1025 },
  { id: "cluj-napoca", name: "Cluj-Napoca", lat: 46.7712, lng: 23.6236 },
  { id: "timisoara", name: "Timișoara", lat: 45.7489, lng: 21.2087 },
  { id: "iasi", name: "Iași", lat: 47.1585, lng: 27.6014 },
  { id: "constanta", name: "Constanța", lat: 44.1598, lng: 28.6348 },
  { id: "craiova", name: "Craiova", lat: 44.3302, lng: 23.7949 },
  { id: "brasov", name: "Brașov", lat: 45.6427, lng: 25.5887 },
  { id: "galati", name: "Galați", lat: 45.4353, lng: 28.008 },
  { id: "ploiesti", name: "Ploiești", lat: 44.9366, lng: 26.0367 },
  { id: "oradea", name: "Oradea", lat: 47.0465, lng: 21.9189 },
  { id: "braila", name: "Brăila", lat: 45.2692, lng: 27.9574 },
  { id: "arad", name: "Arad", lat: 46.1866, lng: 21.3123 },
  { id: "pitesti", name: "Pitești", lat: 44.8565, lng: 24.8692 },
  { id: "sibiu", name: "Sibiu", lat: 45.7983, lng: 24.1256 },
  { id: "bacau", name: "Bacău", lat: 46.567, lng: 26.9146 },
  { id: "targu-mures", name: "Târgu Mureș", lat: 46.5425, lng: 24.5575 },
  { id: "baia-mare", name: "Baia Mare", lat: 47.6533, lng: 23.5795 },
  { id: "buzau", name: "Buzău", lat: 45.15, lng: 26.8203 },
  { id: "botosani", name: "Botoșani", lat: 47.7486, lng: 26.6694 },
  { id: "satu-mare", name: "Satu Mare", lat: 47.7921, lng: 22.8574 },
  { id: "ramnicu-valcea", name: "Râmnicu Vâlcea", lat: 45.0997, lng: 24.3693 },
  { id: "suceava", name: "Suceava", lat: 47.6635, lng: 26.2732 },
  { id: "piatra-neamt", name: "Piatra Neamț", lat: 46.9275, lng: 26.3708 },
  { id: "drobeta-turnu-severin", name: "Drobeta-Turnu Severin", lat: 44.6369, lng: 22.6597 },
  { id: "focsani", name: "Focșani", lat: 45.696, lng: 27.1864 },
  { id: "targoviste", name: "Târgoviște", lat: 44.9247, lng: 25.457 },
  { id: "bistrita", name: "Bistrița", lat: 47.1329, lng: 24.5108 },
  { id: "resita", name: "Reșița", lat: 45.2971, lng: 21.8892 },
  { id: "tulcea", name: "Tulcea", lat: 45.178, lng: 28.805 },
  { id: "slatina", name: "Slatina", lat: 44.4297, lng: 24.362 },
  { id: "calarasi", name: "Călărași", lat: 44.2058, lng: 27.3306 },
  { id: "alba-iulia", name: "Alba Iulia", lat: 46.0733, lng: 23.5805 },
  { id: "deva", name: "Deva", lat: 45.8779, lng: 22.9147 },
  { id: "giurgiu", name: "Giurgiu", lat: 43.9037, lng: 25.9699 },
  { id: "zalau", name: "Zalău", lat: 47.1911, lng: 23.0572 },
  { id: "alexandria", name: "Alexandria", lat: 43.9702, lng: 25.3339 },
  { id: "vaslui", name: "Vaslui", lat: 46.6407, lng: 27.7276 },
  { id: "sfantu-gheorghe", name: "Sfântu Gheorghe", lat: 45.862, lng: 25.7877 },
  { id: "targu-jiu", name: "Târgu Jiu", lat: 45.0362, lng: 23.2745 },
  { id: "slobozia", name: "Slobozia", lat: 44.5647, lng: 27.3661 },
  { id: "miercurea-ciuc", name: "Miercurea Ciuc", lat: 46.3612, lng: 25.8018 },
  { id: "buftea", name: "Buftea", lat: 44.5667, lng: 25.95 },
  // Smaller towns added by the Jul 2026 location cleanup — venues genuinely sit
  // there (far from any county capital), so tagging them here keeps distances
  // honest. Like every city, they stay greyed out until a venue goes visible.
  { id: "campulung-moldovenesc", name: "Câmpulung Moldovenesc", lat: 47.5298, lng: 25.5597 },
  { id: "dorohoi", name: "Dorohoi", lat: 47.9538, lng: 26.397 },
  { id: "sinaia", name: "Sinaia", lat: 45.3455, lng: 25.5481 },
  { id: "sighetu-marmatiei", name: "Sighetu Marmației", lat: 47.9283, lng: 23.8916 },
  { id: "bran", name: "Bran", lat: 45.5163, lng: 25.3716 },
  { id: "gura-humorului", name: "Gura Humorului", lat: 47.5537, lng: 25.889 },
  // Black Sea coast (Jul 2026) — the seaside resorts are distinct destinations
  // with their own venue scenes, not Constanța suburbs, so they get their own
  // slugs. Coords cross-checked against OSM; Mamaia and Eforie use strip/town
  // midpoints (better venue anchors than the administrative nodes).
  { id: "mamaia", name: "Mamaia", lat: 44.2569, lng: 28.6206 },
  { id: "navodari", name: "Năvodari", lat: 44.3186, lng: 28.61 },
  { id: "eforie", name: "Eforie", lat: 44.049, lng: 28.64 },
  { id: "techirghiol", name: "Techirghiol", lat: 44.052, lng: 28.601 },
  { id: "costinesti", name: "Costinești", lat: 43.9536, lng: 28.6317 },
  { id: "neptun-olimp", name: "Neptun-Olimp", lat: 43.862, lng: 28.611 },
  { id: "mangalia", name: "Mangalia", lat: 43.8163, lng: 28.5825 },
  { id: "2mai", name: "2 Mai", lat: 43.79, lng: 28.576 },
  { id: "vama-veche", name: "Vama Veche", lat: 43.7515, lng: 28.576 },
];

/** Default city for new venues and existing rows backfilled by the migration. */
export const DEFAULT_CITY = "cluj-napoca";

const CITY_IDS = new Set(CITIES.map((c) => c.id));

export function isCityId(value: string | null | undefined): value is string {
  return !!value && CITY_IDS.has(value);
}

/** Display name for a city id, falling back to the raw id if unknown. */
export function cityName(id: string): string {
  return CITIES.find((c) => c.id === id)?.name ?? id;
}

/**
 * Public URL slug per city — the `/cluj`-style path used by the indexable
 * content pages and the consumer app. Identity for every city except
 * Cluj-Napoca, whose well-known short form is "cluj". This ONLY changes the URL
 * surface; `restaurants.city` and every query keep using the id, and the four
 * load-bearing ids above are never renamed.
 *
 * ⚠ A vanity slug must never equal a static route segment (landing, terms,
 * privacy, deck, app, go, signin, manage, admin) or it would shadow that page —
 * "cluj" is clear, and all other ids are the full city slug.
 */
export const CITY_VANITY: Record<string, string> = { "cluj-napoca": "cluj" };

const VANITY_TO_ID = new Map<string, string>(CITIES.map((c) => [CITY_VANITY[c.id] ?? c.id, c.id]));

/** City id → URL slug used in paths (`/cluj`, `/iasi`, …). */
export function citySlug(id: string): string {
  return CITY_VANITY[id] ?? id;
}

/**
 * URL slug → city id, or null if the slug isn't a known city / vanity slug.
 * Accepts both the vanity slug (`cluj`) and the raw id (`cluj-napoca`) so old
 * muscle-memory URLs keep resolving; the canonical, prerendered URL is the vanity.
 */
export function cityIdFromSlug(slug: string | null | undefined): string | null {
  if (!slug) return null;
  return VANITY_TO_ID.get(slug) ?? (isCityId(slug) ? slug : null);
}
